/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import Scroll, { ScrollDirection } from '../../store/scroll'
import './style.scss'

interface ScrollElementProps {
  className?: string
  activeClassName?: string
  enterClassName?: string
  onEnterChange?: (
    enter: boolean,
    direction: ScrollDirection,
    y: number
  ) => void
  onActive?: (y: number) => void
  debug?: boolean
  enterOffset?: number
  getRef?: (ref: HTMLDivElement) => void
  onceKey?: string
}

const onceMap: { [key: string]: boolean } = {}

const ScrollElement: React.FC<ScrollElementProps> = ({
  className,
  activeClassName,
  enterClassName,
  children,
  onEnterChange = () => {},
  onActive = () => {},
  getRef,
  onceKey,
  debug = false,
  enterOffset = 0,
}) => {
  const [active, setActive] = useState(false)
  const [enter, setEnter] = useState(false)
  const [isOnce] = useState(onceMap[onceKey ?? '_'] ?? false)
  const dom = useRef<HTMLDivElement>(null)
  const { register } = Scroll.useContainer()

  useEffect(() => {
    const el = dom.current
    if (!el) return
    if ((el.parentNode as HTMLElement).classList.contains('no-effect')) return
    const unregister = register(
      el,
      {
        enter: (d, y) => {
          setEnter(true)
          onEnterChange(true, d, y)
        },
        leave: (d, y) => {
          setEnter(false)
          onEnterChange(false, d, y)
        },
        active: (y) => {
          if (onceKey) {
            onceMap[onceKey] = true
          }
          setActive(true)
          onActive(y)
        },
      },
      {
        debug,
        enterOffset,
      }
    )
    return () => {
      unregister()
    }
  }, [register, onEnterChange, onActive, onceKey, debug, enterOffset])

  useEffect(() => {
    if (getRef) getRef(dom.current!)
    if (onceKey) {
      if (!onceMap[onceKey]) {
        onceMap[onceKey] = false
      }
    }
  }, [])

  return (
    <div
      ref={dom}
      className={classnames(
        'scroll-element',
        className,
        active ? activeClassName : undefined,
        enter ? enterClassName : undefined,
        isOnce ? 'no-active-effect' : undefined
      )}
    >
      {children}
    </div>
  )
}

export default ScrollElement
